/**
 * NotificationDropdown - Desktop notification panel
 * 
 * Facebook-style dropdown with:
 * - Notification list with infinite scroll
 * - Mark as read on view
 * - Click to navigate to content
 * - Real-time updates
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import NotificationItem from './NotificationItem';
import '../styles/Notifications.css';

const NotificationDropdown = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const [viewedIds, setViewedIds] = useState(new Set());
  
  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    markAsRead,
    markAllAsRead,
    loadMore,
  } = useNotifications();

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Mark viewed notifications as read with debounce
  useEffect(() => {
    if (viewedIds.size === 0) return;

    const timeoutId = setTimeout(() => {
      const unreadViewedIds = [...viewedIds].filter(
        id => notifications.find(n => n.id === id && !n.is_read)
      );
      
      if (unreadViewedIds.length > 0) {
        markAsRead(unreadViewedIds);
      }
      setViewedIds(new Set());
    }, 1500); // 1.5 seconds delay before marking as read

    return () => clearTimeout(timeoutId);
  }, [viewedIds, notifications, markAsRead]);

  // Intersection observer for infinite scroll and view tracking
  const handleIntersection = useCallback((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const id = parseInt(entry.target.dataset.notificationId, 10);
        if (id) {
          setViewedIds(prev => new Set([...prev, id]));
        }
      }
    });
  }, []);

  // Set up intersection observer
  useEffect(() => {
    if (!isOpen || !listRef.current) return;

    const observer = new IntersectionObserver(handleIntersection, {
      root: listRef.current,
      threshold: 0.5,
    });

    const items = listRef.current.querySelectorAll('[data-notification-id]');
    items.forEach(item => observer.observe(item));

    return () => observer.disconnect();
  }, [isOpen, notifications, handleIntersection]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !isLoading) {
      loadMore();
    }
  }, [hasMore, isLoading, loadMore]);

  // Navigate to notification target
  const handleNotificationClick = useCallback((notification) => {
    // Mark as read immediately on click
    if (!notification.is_read) {
      markAsRead([notification.id]);
    }

    // Check if this is a chat message notification
    if (notification.extra_data?.type === 'chat_message' && notification.extra_data?.chat_id) {
      navigate('/chat', { state: { chatId: notification.extra_data.chat_id } });
    } else {
      // Navigate based on notification type
      const targetId = notification.target_id || notification.extra_data?.trip_id;
      const targetType = notification.target_type;
      
      if (targetType === 'post' && targetId) {
        navigate(`/posts/${targetId}`);
      } else if (targetType === 'trip' && targetId) {
        if (notification.verb === 'trip_request') {
          navigate(`/companions/${targetId}/candidates`);
        } else {
          navigate(`/companions/${targetId}`);
        }
      } else if (notification.extra_data?.post_id) {
        navigate(`/posts/${notification.extra_data.post_id}`);
      }
    }
    
    onClose();
  }, [navigate, markAsRead, onClose]);

  if (!isOpen) return null;

  return (
    <div className="notification-dropdown" ref={dropdownRef}>
      {/* Header */}
      <div className="notification-dropdown__header">
        <h3 className="notification-dropdown__title">
          Уведомления
          {unreadCount > 0 && (
            <span className="notification-dropdown__count">{unreadCount}</span>
          )}
        </h3>
        {unreadCount > 0 && (
          <button 
            className="notification-dropdown__mark-all"
            onClick={markAllAsRead}
          >
            Прочитать все
          </button>
        )}
      </div>

      {/* Notification list */}
      <div 
        className="notification-dropdown__list"
        ref={listRef}
        onScroll={handleScroll}
      >
        {notifications.length === 0 && !isLoading ? (
          <div className="notification-dropdown__empty">
            <div className="notification-dropdown__empty-icon">🔔</div>
            <p>Нет уведомлений</p>
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={() => handleNotificationClick(notification)}
              />
            ))}
            
            {isLoading && (
              <div className="notification-dropdown__loading">
                <div className="notification-spinner"></div>
              </div>
            )}
            
            {!hasMore && notifications.length > 0 && (
              <div className="notification-dropdown__end">
                Это все уведомления
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationDropdown;

